/**
 * Privacy utilities for analytics
 * Ensures no PII or sensitive financial data is sent to analytics
 */

import crypto from 'crypto';
import type { AmountRange } from './types';

/**
 * Hash a user ID or other identifier
 * Uses SHA-256 for consistent anonymization
 */
export function hashIdentifier(identifier: string): string {
  if (!identifier) return '';
  return crypto.createHash('sha256').update(identifier).digest('hex');
}

/**
 * Convert exact amount to range for privacy
 */
export function anonymizeAmount(amount: number): AmountRange {
  if (amount < 10) return 'under_10';
  if (amount < 50) return '10_to_50';
  if (amount < 100) return '50_to_100';
  if (amount < 500) return '100_to_500';
  if (amount < 1000) return '500_to_1000';
  if (amount < 5000) return '1000_to_5000';
  return 'over_5000';
}

/**
 * Remove PII from transaction description
 */
export function sanitizeDescription(description: string): string {
  if (!description) return 'transaction';
  
  // Remove common PII patterns
  let sanitized = description;
  
  // Remove email addresses
  sanitized = sanitized.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]');
  
  // Remove phone numbers (various formats)
  sanitized = sanitized.replace(/(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, '[phone]');
  
  // Remove credit card numbers (basic pattern)
  sanitized = sanitized.replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, '[card]');
  
  // Remove SSN-like patterns
  sanitized = sanitized.replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[ssn]');
  
  // Remove account numbers (6+ digits)
  sanitized = sanitized.replace(/\b\d{6,}\b/g, '[account]');
  
  // Truncate if too long
  if (sanitized.length > 50) {
    sanitized = sanitized.substring(0, 47) + '...';
  }
  
  return sanitized;
}

/**
 * Sanitize payee name to remove PII
 */
export function sanitizePayee(payee: string): string {
  if (!payee) return 'unknown';
  
  // Common patterns to preserve
  const commonPayees = [
    'amazon', 'walmart', 'target', 'costco', 'starbucks',
    'uber', 'lyft', 'netflix', 'spotify', 'apple',
    'google', 'microsoft', 'grocery', 'restaurant', 'gas'
  ];
  
  const lowerPayee = payee.toLowerCase();
  
  // Check if it's a common merchant
  for (const common of commonPayees) {
    if (lowerPayee.includes(common)) {
      return common;
    }
  }
  
  // For others, just return a category
  if (lowerPayee.includes('bank') || lowerPayee.includes('credit')) {
    return 'financial_institution';
  }
  
  if (lowerPayee.includes('store') || lowerPayee.includes('shop')) {
    return 'retail_store';
  }
  
  if (lowerPayee.includes('restaurant') || lowerPayee.includes('cafe')) {
    return 'food_service';
  }
  
  // Default to generic
  return 'merchant';
}

/**
 * Sanitize error messages to remove sensitive data
 */
export function sanitizeErrorMessage(error: string): string {
  if (!error) return 'unknown_error';
  
  let sanitized = error;
  
  // Remove file paths
  sanitized = sanitized.replace(/\/[^\s]+/g, '[path]');
  
  // Remove URLs
  sanitized = sanitized.replace(/https?:\/\/[^\s]+/g, '[url]');
  
  // Remove email addresses
  sanitized = sanitized.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]');
  
  // Remove IDs and tokens
  sanitized = sanitized.replace(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '[uuid]');
  sanitized = sanitized.replace(/Bearer\s+[^\s]+/g, 'Bearer [token]');
  
  // Truncate if too long
  if (sanitized.length > 200) {
    sanitized = sanitized.substring(0, 197) + '...';
  }
  
  return sanitized;
}

/**
 * Get browser/device info without fingerprinting
 */
export function getDeviceInfo(): Record<string, any> {
  if (typeof window === 'undefined') return {};
  
  return {
    screen_size: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    platform: navigator.platform,
    // Don't include user agent to avoid fingerprinting
  };
}

/**
 * Sanitize URL to remove sensitive query params
 */
export function sanitizeUrl(url: string): string {
  if (!url) return '';
  
  try {
    const urlObj = new URL(url);
    
    // Remove sensitive query params
    const sensitiveParams = ['token', 'key', 'secret', 'password', 'auth', 'session'];
    
    sensitiveParams.forEach(param => {
      urlObj.searchParams.delete(param);
    });
    
    return urlObj.toString();
  } catch {
    // If URL parsing fails, return path only
    return url.split('?')[0];
  }
}

/**
 * Check if we should track this event based on privacy settings
 */
export function shouldTrackEvent(eventCategory: string, consent: { analytics?: boolean; marketing?: boolean; essential?: boolean; [key: string]: any }): boolean {
  // Essential events are always tracked
  if (eventCategory === 'error' || eventCategory === 'performance') {
    return consent.essential !== false;
  }
  
  // Marketing events require marketing consent
  if (eventCategory === 'conversion') {
    return consent.marketing === true;
  }
  
  // All other events require analytics consent
  return consent.analytics === true;
}